import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const STORAGE_KEY = "uh-reserve-state-v1";

const ReserveContext = createContext(null);

const loadInitial = () => {
  if (typeof window === "undefined") return { reserve: [], obsession: [] };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { reserve: [], obsession: [] };
    const parsed = JSON.parse(raw);
    return {
      reserve: Array.isArray(parsed.reserve) ? parsed.reserve : [],
      obsession: Array.isArray(parsed.obsession) ? parsed.obsession : [],
    };
  } catch {
    return { reserve: [], obsession: [] };
  }
};

export function ReserveProvider({ children }) {
  const [state, setState] = useState(loadInitial);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* private mode — silently ignore */
    }
  }, [state]);

  const addToReserve = useCallback((item) => {
    setState((s) => {
      if (s.reserve.some((it) => it.id === item.id)) return s;
      return {
        reserve: [...s.reserve, { ...item, addedAt: new Date().toISOString() }],
        obsession: s.obsession.filter((it) => it.id !== item.id),
      };
    });
  }, []);

  const removeFromReserve = useCallback((id) => {
    setState((s) => ({ ...s, reserve: s.reserve.filter((it) => it.id !== id) }));
  }, []);

  const clearReserve = useCallback(() => {
    setState((s) => ({ ...s, reserve: [] }));
  }, []);

  const addToObsession = useCallback((item) => {
    setState((s) => {
      if (s.obsession.some((it) => it.id === item.id)) return s;
      if (s.reserve.some((it) => it.id === item.id)) return s; // already in reserve
      return { ...s, obsession: [...s.obsession, { ...item, addedAt: new Date().toISOString() }] };
    });
  }, []);

  const removeFromObsession = useCallback((id) => {
    setState((s) => ({ ...s, obsession: s.obsession.filter((it) => it.id !== id) }));
  }, []);

  const moveToReserveFromObsession = useCallback((id) => {
    setState((s) => {
      const item = s.obsession.find((it) => it.id === id);
      if (!item) return s;
      if (s.reserve.some((it) => it.id === id)) {
        return { ...s, obsession: s.obsession.filter((it) => it.id !== id) };
      }
      return {
        reserve: [...s.reserve, { ...item, addedAt: new Date().toISOString() }],
        obsession: s.obsession.filter((it) => it.id !== id),
      };
    });
  }, []);

  const value = useMemo(
    () => ({
      reserve: state.reserve,
      obsession: state.obsession,
      reserveCount: state.reserve.length,
      obsessionCount: state.obsession.length,
      isInReserve: (id) => state.reserve.some((it) => it.id === id),
      isInObsession: (id) => state.obsession.some((it) => it.id === id),
      addToReserve,
      removeFromReserve,
      clearReserve,
      addToObsession,
      removeFromObsession,
      moveToReserveFromObsession,
    }),
    [state, addToReserve, removeFromReserve, clearReserve, addToObsession, removeFromObsession, moveToReserveFromObsession]
  );

  return <ReserveContext.Provider value={value}>{children}</ReserveContext.Provider>;
}

export function useReserve() {
  const ctx = useContext(ReserveContext);
  if (!ctx) throw new Error("useReserve must be used inside <ReserveProvider>");
  return ctx;
}
